import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
} from '@mui/material';
import { useState } from 'react';

import { TermsAndPrivacyLinks } from '@/features/identity/components/TermsAndPrivacyLinks';

export interface GoogleConsentDialogProps {
  open: boolean;
  loading: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * The consent step for a Google sign-in that turned out to be a first-time registration (ADR-0026).
 * "Continue with Google" doubles as sign-up, so there is no way to know in advance whether the person
 * is about to create an account — the backend tells us only after the token exchange, by refusing with
 * `Legal.AcceptanceRequired`. This dialog is what `SocialLoginButtons` shows in response, and confirming
 * it retries the SAME provider access token with `termsAccepted: true` — Google's token stays valid for
 * the few seconds that takes, so nothing is re-requested from Google.
 *
 * An existing account signing in again never sees this: the backend only asks on account creation.
 */
export function GoogleConsentDialog({ open, loading, onConfirm, onCancel }: GoogleConsentDialogProps) {
  const [checked, setChecked] = useState(false);

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth>
      <DialogTitle>Before you continue</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          This Google account isn't linked to a Willnq account yet. Continuing will create one.
        </DialogContentText>
        <FormControlLabel
          control={
            <Checkbox checked={checked} onChange={(event) => setChecked(event.target.checked)} />
          }
          label={
            <>
              I agree to the <TermsAndPrivacyLinks />.
            </>
          }
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={onConfirm} variant="contained" disabled={!checked || loading}>
          Continue
        </Button>
      </DialogActions>
    </Dialog>
  );
}
